import { Container, Input, Stack, Text } from '@chakra-ui/react';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { toaster } from '@app/components/chakra/primitives/toaster';
import { useAdminConfig } from '@app/hooks/use-admin-config';
import { NAMESPACE } from '@app/i18n';
import {
  useGetIntegrationConfigQuery,
  useUpdateIntegrationConfigMutation,
} from '@app/services/integration-config';
import { LayoutAdminPage } from '@chakra-composites/layout-admin-page';
import { LayoutAdminPageHeader } from '@chakra-composites/layout-admin-page-header';
import { Field } from '@chakra-primitives/field';

import { TOASTER_DURATION } from '../../shared/constants';
import { usePartnerContext } from '../../shared/use-partner-context';
import { validateMappingValue } from '../../shared/validation';

const MappingPage = () => {
  const { t } = useTranslation(NAMESPACE.INTEGRATIONS);
  const config = useAdminConfig();
  const navigate = useNavigate();
  const { partnerName, displayName } = usePartnerContext();

  const { data: configData } = useGetIntegrationConfigQuery(partnerName);
  const mappings = (configData?.integrationMappings || []) as {
    integrationId?: string;
  }[];
  const currentMappingId =
    mappings.length > 0 ? (mappings[0]?.integrationId ?? '') : '';

  const [mappingValue, setMappingValue] = useState(currentMappingId);
  const [isEditing, setIsEditing] = useState(false);

  const { mutate: updateConfig, isLoading: isSaving } =
    useUpdateIntegrationConfigMutation(
      partnerName,
      () => {
        toaster.create({
          type: 'success',
          title: t('mappingPage.saveSuccessTitle'),
          description: t('mappingPage.saveSuccessDescription'),
          duration: TOASTER_DURATION,
        });
        setIsEditing(false);
      },
      (error) => {
        toaster.create({
          type: 'error',
          title: t('mappingPage.saveErrorTitle'),
          description: (error as Error).message,
          duration: TOASTER_DURATION,
        });
      },
    );

  useEffect(() => {
    if (currentMappingId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMappingValue(currentMappingId);
    }
  }, [currentMappingId]);

  const handleChange = (value: string) => {
    setMappingValue(value);
    setIsEditing(value !== currentMappingId && validateMappingValue(value));
  };

  const handleSave = () => {
    updateConfig({
      configSetting: 'mapping',
      configData: { integrationId: mappingValue },
    });
  };

  const handleCancel = () => {
    setMappingValue(currentMappingId);
    setIsEditing(false);
    navigate(`/admin/integrations/${partnerName}`);
  };

  const isInvalid = !!mappingValue && !validateMappingValue(mappingValue);

  return (
    <LayoutAdminPage
      breadcrumbs
      breadcrumbSegmentOverrides={{ 1: displayName }}
      isDirty={isEditing}
      header={
        <LayoutAdminPageHeader
          title={config.title}
          description={config.description}
          isEditing={isEditing}
          isSaving={isSaving}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      }
      childrenScroll
      dataTestId={`${partnerName}-mapping-admin`}
    >
      <Container maxW="lg" mt={6}>
        <Stack gap="4" w="full">
          <Stack gap="0" w="full">
            <Text fontSize="md" color="fg">
              {t('steps.mapping.headerTitle', { displayName })}
            </Text>
            <Text fontSize="sm" color="fg.muted">
              {t('mappingPage.headerDescription', { displayName })}
            </Text>
          </Stack>

          <Field
            label={t('summaries.mapping.mappingIdLabel')}
            required
            w="full"
            invalid={isInvalid}
            errorText={isInvalid ? t('mappingPage.errorMessage') : undefined}
            helperText={!isInvalid ? t('mappingPage.errorMessage') : undefined}
          >
            <Input
              type="text"
              value={mappingValue}
              onChange={(e) => handleChange(e.target.value)}
              placeholder={t('mappingPage.placeholder')}
              size="md"
              data-testid="mapping-value-input"
            />
          </Field>
        </Stack>
      </Container>
    </LayoutAdminPage>
  );
};

export default MappingPage;
